/**
 * Production verification page frame.
 *
 * Extracted from the prototype PageShell/PageHeader (ui.tsx) for WP1c (#46599).
 * Page-family modules import this instead of prototypes/verification/ui.tsx so
 * later WP6 workers do not edit the coupled prototype kit.
 *
 * Product mode: no Prototype copy, no fixture store, no splat resolver.
 */

import type { ReactNode } from "react";
import { EmptyState } from "@excellent/ui";
import { ApiError } from "@excellent/ui/api";
import { href } from "../nav";
import { useVerificationTopBar } from "../shell";
import { ActionButton, Callout } from "../ui";

export type VerificationPageProps = {
  onNavigate: (to: string) => void;
};

export type VerificationDetailPageProps = VerificationPageProps & {
  id: string;
};

export type PageFrameProps = {
  /** Ignored. Page-title eyebrows (Decide / Operate / …) are retired. */
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  /** Stable hook for later WP6/WP12 action-manifest inventory. */
  page: string;
  children?: ReactNode;
};

export function PageFrame({
  title,
  lede,
  actions,
  wide,
  page,
  children,
}: PageFrameProps) {
  useVerificationTopBar({
    detailTitle:
      typeof title === "string" || typeof title === "number"
        ? String(title)
        : undefined,
  });

  return (
    <div
      className="vp-page flex flex-col gap-4"
      data-wide={wide ? "true" : undefined}
      data-verification-page={page}
    >
      <header className="vp-header">
        <div className="vp-header-text">
          <h1 className="vp-h1">{title}</h1>
          {lede ? <p className="vp-lede">{lede}</p> : null}
        </div>
        {actions ? <div className="vp-header-actions">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
}

export function PagePending({
  title,
  children,
  onRetry,
}: {
  title: string;
  children: ReactNode;
  onRetry?: () => void;
}) {
  const state = classifyVerificationPageState(title, children);
  const alert =
    state === "error" || state === "timeout" || state === "permission"
      ? "assertive"
      : "polite";
  const busy = state === "loading";
  const action = onRetry ? (
    <ActionButton actionId="page-state.retry" onClick={onRetry}>
      Retry
    </ActionButton>
  ) : undefined;

  return (
    <section
      className="vp-page-state"
      data-verification-ui-state={state}
      data-resilience-state={state}
      data-state={state}
      aria-live={alert}
      aria-busy={busy}
    >
      {state === "degraded" ? (
        <Callout tone="unknown" title="Degraded state">
          Primary data is incomplete. This page is showing the safest available projection.
        </Callout>
      ) : null}
      <EmptyState
        variant="card"
        title={title}
        description={children}
        action={action}
        className="vp-page-state-card"
      />
    </section>
  );
}

function errorMessage(err: unknown): string {
  if (typeof err === "string") return err;
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "The request did not complete.";
}

export function PageError({
  title,
  error,
  onRetry,
  actionId = "api.retry",
}: {
  title: string;
  error: unknown;
  onRetry?: () => void;
  actionId?: string;
}) {
  return (
    <div data-page-error="true" data-page-error-action-id={actionId}>
      <PagePending
        title={title}
        onRetry={
          onRetry
            ? () => {
                onRetry();
              }
            : undefined
        }
      >
        {errorMessage(error)}
      </PagePending>
    </div>
  );
}

export type VerificationPageState =
  | "loading"
  | "empty"
  | "partial"
  | "degraded"
  | "timeout"
  | "permission"
  | "error";

export function classifyVerificationPageState(
  title: string,
  children: ReactNode,
): VerificationPageState {
  const text = `${title} ${flattenText(children)}`.toLowerCase();
  if (/\b(loading|fetching|reading|resolving|reconciling)\b/.test(text)) return "loading";
  if (/\b(timeout|timed out)\b/.test(text)) return "timeout";
  if (/\b(gated|permission|not authorized|access|reviewer|grant)\b/.test(text)) return "permission";
  if (/\b(unavailable|could not load|did not complete|error|failed)\b/.test(text)) return "error";
  if (/\b(degraded|incomplete|limitation|partial)\b/.test(text)) return "degraded";
  if (/\b(no real|not found|no .*loaded|no .*yet|empty|nothing|choose|not configured|has not)\b/.test(text)) {
    return "empty";
  }
  return "partial";
}

function flattenText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenText).join(" ");
  if (typeof node === "object" && "props" in node) {
    return flattenText((node as { props?: { children?: ReactNode } }).props?.children);
  }
  return "";
}

export function PageLink({
  onNavigate,
  to,
  children,
}: {
  onNavigate: (to: string) => void;
  to: string;
  children: string;
}) {
  const path = href(to);
  return (
    <a
      href={path}
      onClick={(e) => {
        e.preventDefault();
        onNavigate(path);
      }}
    >
      {children}
    </a>
  );
}
